//
// Clearing everything the previous user left behind.
//
// The stores here are module-level signals. A logout does not reload the page,
// so they survive it: signing out and signing in as somebody else left the
// previous user's tabs open, their sidebars in place and their project data in
// memory. Whoever logged in next could read all of it, regardless of what they
// were allowed to see. The server was never the problem — the data had already
// been fetched and was simply never dropped.
//
// Called on both sign-out and sign-in. Sign-in matters on its own: a session can
// end without anyone pressing "log out" (expiry, a cleared token, a second tab),
// and then the sign-out path never runs.
//

import { ETabSet, activeContext, activeKey, activeKeys, lastNonSidebarContext, tabs } from './tabsStore'
import { sidebarVisible, sidebars } from './sidebarStore'
import projectStore from './projectStore'
import { resetSettings } from './settingsStore'
import { clearProgress } from './progressStore'

// Defined in components/Layout/LayoutManager. Kept as literals rather than
// imported, so this module does not pull the whole layout component in.
const STORAGE_KEY_LAYOUT = 'saved-layout'
const STORAGE_KEY_TABS = 'saved-tabs'

/**
 * Drops all user-specific UI state.
 *
 * Deliberately unconditional: it does not compare user ids and does not try to
 * keep anything "harmless". A tab holds the data it was opened with, and there
 * is no cheap way to tell which of those the next user may see.
 */
export function resetClientState(): void {
    // open tabs and everything addressing them
    tabs.value = []
    activeKey.value = ''
    activeKeys.value = {}
    activeContext.value = ETabSet.MAINTAB
    lastNonSidebarContext.value = ETabSet.MAINTAB

    // sidebars
    sidebars.value = []
    sidebarVisible.value = false

    // the current project and its cached lists (templates, data lists, converters)
    projectStore.clearProject()

    // per-user settings held in memory
    resetSettings()

    // the tenant's tasks, file names and all — the next user may belong to a
    // different tenant, and the store is only refilled from what that one sees
    clearProgress()

    // LayoutManager restores tabs from localStorage on mount, so leaving these
    // behind would bring the previous user's tabs back on the next page load.
    if (typeof window !== 'undefined') {
        window.localStorage.removeItem(STORAGE_KEY_TABS)
        window.localStorage.removeItem(STORAGE_KEY_LAYOUT)
    }
}
